import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { CacheService } from "./cache.service";
import { UserObj } from "./global-service.service";
import { AuthService } from "./auth/auth.service";

@Injectable({ providedIn: "root" })
export class UserProfileService {
  private controllerName = "Users";

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private authService: AuthService
  ) {}

  // ==================== CACHED METHODS ====================

  /**
   * Versione cached di get_all_users - usa cache per 5 minuti
   */
  getCachedAllUsers(): Observable<any> {
    const key = "all_users";
    return this.cacheService.get(
      key,
      () => this.get_all_users(),
      15 * 60 * 1000
    );
  }

  /**
   * Versione cached di get_user_by_id - usa cache per 5 minuti
   */
  getCachedUserById(userId: string): Observable<any> {
    const key = this.cacheService.createKey("user_by_id", userId);
    return this.cacheService.get(
      key,
      () => this.get_user_by_id(userId, ""),
      15 * 60 * 1000
    );
  }

  /**
   * Invalida la cache degli utenti (da chiamare dopo update)
   */
  invalidateUsersCache(): void {
    this.cacheService.invalidateByPrefix("all_users");
    this.cacheService.invalidateByPrefix("user_by_id");
    this.cacheService.invalidateByPrefix("pending_users");
    this.cacheService.invalidateByPrefix("admin_users");
    this.cacheService.invalidateByPrefix("customer_users");
  }

  get_all_users(): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetAllUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_pending_users(): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetPendingUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_admin_users(): Observable<any> {
    if (!this.authService.isSuperUser()) {
      return of([]);
    }
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetAdminUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_customer_users(): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetCustomerUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_plantadmin_users(): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetPlantAdminUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_mobile_users(): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetMobileUsers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_user_by_id(user_id: string, email: string): Observable<UserObj> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/GetUserById";
    url += "?userId=" + user_id + "&email=" + email;
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  update_user(userUpdateRequest: any): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/UpdateUser";
      const headers = new HttpHeaders({
        "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
        "Content-Type": "application/json" 
      });

      const payload = {
        id: userUpdateRequest.id,
        azUniqueId: userUpdateRequest.azUniqueId,
        plantRef: userUpdateRequest.plantRef,
        plantId: userUpdateRequest.plantId
      };
      return this.http.post<any>(url, payload, { headers }).pipe(
        tap(() => {
          // Invalida cache dopo update utente
          this.invalidateUsersCache();
        }),
        catchError((error) => {
          console.error("API Error: ", error);
          throw error;
        })
      );
  }

  update_user_pending(user: UserObj, pending_adds: any): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/UpdateUserPending";
      const headers = new HttpHeaders({
        "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      });
      const payload = { id: user.id, pending_adds: pending_adds };

      return this.http.post<any>(url, payload, { headers }).pipe(
        tap(),
        catchError((error) => {
          console.error("API Error: ", error);
          throw error;
        })
      );
  }

  update_customer_code(user: UserObj, customer_id: any): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/UpdateCustomerCode";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });
    const payload = { id: user.id, customer_id: customer_id };

    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  upgrade_to_plantmaster(userId: string, master: boolean): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/UpgradeToPlantMaster";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });
    const payload = { id: userId, master: master };

    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  add_mobile_user(
    active: boolean,
    password: string,
    username: string
  ): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/AddMobileUser";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });
    const payload = { active: 1, password: password, username: username };

    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  delete_mobile_user(userId: number): Observable<any> {
    var url: string =
      environment.baseUrl + "/" + this.controllerName + "/DeleteMobileUser";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.post<any>(url, userId, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  // ==================== PLANTS MANAGEMENT ====================

  get_plants(): Observable<any> {
    var url: string = environment.baseUrl + "/Plants";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_user_plants(userId: string): Observable<string[]> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/plants";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<string[]>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  assign_user_plants(userId: string, plantIds: string[], assignedBy: string): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/plants";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      "Content-Type": "application/json"
    });
    const payload = { plantIds: plantIds, assignedBy: assignedBy };

    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  update_user_plants(userId: string, plantIds: string[], assignedBy: string): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/plants";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      "Content-Type": "application/json"
    });
    const payload = { plantIds: plantIds, assignedBy: assignedBy };

    return this.http.put<any>(url, payload, { headers }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  remove_user_plants(userId: string): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/plants";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.delete<any>(url, { headers }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  // ==================== CUSTOMERS MANAGEMENT ====================

  get_customers(): Observable<any> {
    var url: string = environment.baseUrl + "/Customers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  get_user_customers(userId: string): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/customers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
    });

    return this.http.get<any>(url, { headers }).pipe(
      tap(),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  assign_user_customers(userId: string, customerIds: number[], assignedBy: string): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/customers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      "Content-Type": "application/json"
    });
    const payload = { customerIds: customerIds, assignedBy: assignedBy };

    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  remove_user_customers(userId: string, customerIds: number[]): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/customers";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      "Content-Type": "application/json"
    });
    const payload = { customerIds: customerIds };

    // Use http.request for DELETE with body
    return this.http.request<any>('DELETE', url, {
      headers: headers,
      body: payload
    }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }

  // ==================== USER STATUS MANAGEMENT ====================

  update_user_status(userId: string, status: number): Observable<any> {
    var url: string = environment.baseUrl + "/" + this.controllerName + "/" + userId + "/status";
    const headers = new HttpHeaders({
      "Ocp-Apim-Subscription-Key": environment.subscriptionKey,
      "Content-Type": "application/json"
    });
    const payload = { status: status };

    return this.http.put<any>(url, payload, { headers }).pipe(
      tap(() => {
        this.invalidateUsersCache();
      }),
      catchError((error) => {
        console.error("API Error: ", error);
        throw error;
      })
    );
  }
}
